import {
  Box,
  Card,
  CardActionArea,
  Skeleton,
  SvgIcon,
  useTheme,
} from "@mui/material";
import { ReactComponent as AddIcon } from "../assets/IconAdd.svg";
import { ReactComponent as EmptyIcon } from "../assets/IconEmptyDark.svg";
import { seeAllCardBg } from "./Carousel";

/**
 * A poster-sized slot in a collection, drawn as a flat skeleton with an icon
 * centred on it: an add button for the owner, or (when readOnly) a low-key
 * empty marker — the empty-box illustration flattened to one muted tone, as
 * in EmptySectionState. The skeleton gives the icon a backdrop that differs
 * from any surface it sits on, so the same secondary tone works on the page
 * and on secondary-coloured cards alike.
 *
 * `matchSeeAll`: styled like the carousel "See all" card it sits beside — that
 * card's background and shadow, with the add icon in the primary colour.
 */
export default function AddorEmptyTile({
  onClick,
  readOnly,
  matchSeeAll = false,
}) {
  const theme = useTheme();

  const cardContent = (
    <>
      <Skeleton
        variant="rectangular"
        animation={false}
        sx={{
          width: "100%",
          height: "0",
          paddingTop: "150%",
          borderRadius: 1,
          ...(matchSeeAll && { bgcolor: seeAllCardBg }),
        }}
      />
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          display: "flex",
          ...(readOnly && { width: "60%" }),
        }}
      >
        {readOnly ? (
          <SvgIcon
            component={EmptyIcon}
            inheritViewBox
            aria-hidden
            sx={{
              width: "100%",
              height: "auto",
              color: theme.palette.secondary.main,
              "& path[fill]": { fill: "currentColor" },
              '& path[fill="#ff0000"]': {
                fill: theme.palette.background.default,
              },
            }}
          />
        ) : (
          <SvgIcon
            component={AddIcon}
            inheritViewBox
            fontSize="large"
            sx={{
              color: matchSeeAll
                ? theme.palette.primary.main
                : theme.palette.secondary.contrastText,
            }}
          />
        )}
      </Box>
    </>
  );

  return (
    <Card
      onClick={!readOnly ? onClick : undefined}
      // matchSeeAll: the "See all" card's shadow too (elevation 1, without the
      // dark-mode elevation overlay, which would tint the background)
      elevation={matchSeeAll ? 1 : 0}
      sx={{
        width: "100%",
        position: "relative",
        height: "100%",
        bgcolor: "inherit",
        backgroundImage: "none",
      }}
    >
      {!readOnly ? <CardActionArea>{cardContent}</CardActionArea> : cardContent}
    </Card>
  );
}
